<template>
  <div class="analisi-reclami">
    <div class="header-section">
      <h2 class="page-title">Gestione Reclami</h2>
      <p class="subtitle">Gestione e tracciamento dei reclami</p>
    </div>

    <div class="form-section">
      <div class="section-header">
        <h3>Inserimento Reclamo</h3>
      </div>

      <div class="form-container">
        <div class="form-group">
          <label for="categoria" class="form-label">Categoria <span class="required">*</span></label>
          <Dropdown id="categoria" v-model="formData.categoria" :options="categorie" optionLabel="descrizione"
            placeholder="Seleziona una categoria" class="w-full" @change="onCategoriaChange" />
        </div>

        <div class="form-group">
          <label for="sottoCategoria" class="form-label">Sotto Categoria <span class="required">*</span></label>
          <Dropdown id="sottoCategoria" v-model="formData.sottoCategoria" :options="sottoCategorie"
            optionLabel="descrizione" placeholder="Seleziona una sotto categoria" class="w-full"
            :disabled="!formData.categoria" @change="onSottoCategoriaChange" />
        </div>

        <div class="form-group">
          <label for="servizio" class="form-label">Servizio Cliente/Agenzia <span class="required">*</span></label>
          <InputText id="servizio" v-model="formData.servizioNome" placeholder="Inserisci nome servizio/cliente"
            class="w-full" :disabled="!formData.sottoCategoria" />
        </div>
        <div class="form-group">
          <label for="nFatture" class="form-label">Numero Fattura</label>
          <InputText id="nFatture" v-model="formData.nFatture" placeholder="Inserisci numero fattura" class="w-full"
            :disabled="!formData.sottoCategoria" />
        </div>
        <div class="dates-container">
          <div class="form-group">
            <label for="inizioGestione" class="form-label">Inizio Gestione <span class="required">*</span></label>
            <Calendar id="inizioGestione" v-model="formData.inizioGestione" showTime hourFormat="24"
              :disabled="!formData.servizioNome" placeholder="Seleziona data e ora" />
          </div>

          <div class="form-group">
            <label for="fineGestione" class="form-label">Fine Gestione</label>
            <Calendar id="fineGestione" v-model="formData.fineGestione" showTime hourFormat="24"
              :disabled="!formData.inizioGestione" placeholder="Seleziona data e ora" />
          </div>
        </div>

        <div class="form-group">
          <label for="descrizione" class="form-label">Descrizione <span class="required">*</span></label>
          <Textarea id="descrizione" v-model="formData.descrizione" :disabled="!formData.inizioGestione" rows="5"
            placeholder="Inserisci una descrizione dettagliata del reclamo" class="w-full" />
        </div>

        <div class="action-buttons">
          <Button label="Annulla" icon="pi pi-times" class="p-button-danger" @click="resetForm" />
          <Button label="Salva Reclamo" icon="pi pi-check" class="p-button-success" :disabled="!isFormValid"
            @click="salvaReclamo" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useToast } from 'primevue/usetoast';
import axios from 'axios';
import { useStore } from 'vuex';

export default {
  name: 'GestioneReclami',
  setup() {
    const toast = useToast();
    const loading = ref(false);
    const store = useStore();
    const currentUser = ref(store.state.auth.user);
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;

    const apiBaseUrl = process.env.VUE_APP_ASK_PRATICHE_URL;

    const formData = ref({
      categoria: null,
      sottoCategoria: null,
      servizioNome: '',
      inizioGestione: null,
      fineGestione: null,
      descrizione: '',
      nFatture: '',
      codCliente: '',
      codAgente: '',
      codRapp: '',
      utente: currentUser.value ? currentUser.value.email : '',
    });

    const categorie = ref([]);
    const sottoCategorie = ref([]);

    onMounted(async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/reclami/categorie`);
        categorie.value = response.data;
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare le categorie dal database',
          life: 3000
        });
      }
    });

    const onCategoriaChange = async () => {
      formData.value.sottoCategoria = null;
      formData.value.servizioNome = '';
      formData.value.inizioGestione = null;
      formData.value.fineGestione = null;
      formData.value.descrizione = '';

      if (formData.value.categoria) {
        try {
          const response = await axios.get(`${apiBaseUrl}/api/reclami/categorie/${formData.value.categoria.id}/sottocategorie`);

          sottoCategorie.value = response.data;
        } catch (error) {
          sottoCategorie.value = [];
        }
      } else {
        sottoCategorie.value = [];
      }
    };

    const onSottoCategoriaChange = () => {
      formData.value.servizioNome = '';
      formData.value.inizioGestione = null;
      formData.value.fineGestione = null;
      formData.value.descrizione = '';
    };

    const isFormValid = computed(() => {
      return (
        formData.value.categoria &&
        formData.value.sottoCategoria &&
        formData.value.servizioNome &&
        formData.value.servizioNome.trim() !== '' &&
        formData.value.inizioGestione &&
        formData.value.descrizione &&
        formData.value.descrizione.trim() !== ''
      );
    });

    const resetForm = () => {
      formData.value = {
        categoria: null,
        sottoCategoria: null,
        servizioNome: '',
        inizioGestione: null,
        fineGestione: null,
        descrizione: '',
        utente: '',
        codCliente: '',
        codAgente: '',
        codRapp: ''
      };
      sottoCategorie.value = [];

      // toast.add({
      //   severity: 'info',
      //   summary: 'Form Annullato',
      //   detail: 'Il form è stato ripristinato',
      //   life: 3000
      // });
    };

    const salvaReclamo = async () => {
      loading.value = true;

      try {
        const datiReclamo = {
          cod_sottocategoria: formData.value.sottoCategoria.id,
          utente: formData.value.utente || 'utente_default',
          descrizione: formData.value.descrizione,
          inizio: formData.value.inizioGestione,
          fine: formData.value.fineGestione,
          nome_servizio: formData.value.servizioNome,
          n_fatture: formData.value.nFatture,
          cod_cliente: formData.value.codCliente,
          cod_agente: formData.value.codAgente,
          cod_rapp: formData.value.codRapp
        };

        const response = await axios.post(`${apiBaseUrl}/api/reclami`, datiReclamo);

        toast.add({
          severity: 'success',
          summary: 'Reclamo Salvato',
          detail: 'Il reclamo è stato registrato con successo nel database',
          life: 3000
        });

        resetForm();
      } catch (error) {
        console.error('Errore nel salvataggio del reclamo:', error);
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: error.response?.data?.message || 'Impossibile salvare il reclamo nel database',
          life: 3000
        });
      } finally {
        loading.value = false;
      }
    };

    return {
      formData,
      categorie,
      sottoCategorie,
      loading,
      isFormValid,
      onCategoriaChange,
      onSottoCategoriaChange,
      resetForm,
      salvaReclamo
    };
  }
};
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');

.analisi-reclami {
  font-family: 'Montserrat', sans-serif;
  padding: 1.5rem;
}

.header-section {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.subtitle {
  color: #666;
  font-size: 1rem;
}

.section-header {
  border-bottom: 1px solid #ddd;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
}

.section-header h3 {
  font-weight: 500;
  font-size: 1.3rem;
  color: #444;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  color: #555;
}

.required {
  color: #e74c3c;
}

.dates-container {
  display: flex;
  gap: 1rem;
}

.dates-container .form-group {
  flex: 1;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

@media (max-width: 768px) {
  .dates-container {
    flex-direction: column;
  }
}
</style>